import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Container,
  TextField,
  Typography,
} from "@mui/material";
import React, { useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";
import {
  confirmSignUp,
  resendConfirmationCode,
  signIn,
} from "../lib/services/authService";

const ConfirmAccountPage: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [email, setEmail] = useState(location.state?.email || "");
  const [password, setPassword] = useState("");
  const [confirmationCode, setConfirmationCode] = useState("");
  const [error, setError] = useState("");
  const [successMessage, setSuccessMessage] = useState("");
  const [loading, setLoading] = useState(false);
  const [isFromLogin, setIsFromLogin] = useState(false);

  const { refreshAuth, user } = useAuth();

  React.useEffect(() => {
    const params = new URLSearchParams(location.search);
    const emailParam = params.get("email");
    if (emailParam) setEmail(emailParam);

    // Check if user came from login page (via state)
    if (location.state?.fromLogin) {
      setIsFromLogin(true);
      if (location.state?.email) {
        setEmail(location.state.email);
      }
      setSuccessMessage(
        "A new verification code has been sent to your email. Please enter it below to verify your account.",
      );
    }
    // Or check if user is already logged in (redirected from ProtectedRoute with unverified email)
    else if (user && user.emailVerified === false) {
      setIsFromLogin(true);
      setEmail(user.email);
      setSuccessMessage(
        "A verification code has been sent to your email. Please enter it below to verify your account.",
      );
    }

    if (params.get("success")) {
      // After a successful payment, navigate to trees page to verify subscription there
      localStorage.removeItem("stripe_product_type");
      navigate("/trees", {
        state: {
          verifySubscription: true,
        },
      });
    }

    if (params.get("cancel") || params.get("error")) {
      // On cancellation/failure, direct user to subscription management to retry
      localStorage.removeItem("stripe_product_type");
      navigate("/subscription", {
        state: {
          stripeError: "Payment processing failed, please try again later",
        },
      });
    }
  }, [location.search, location.state, navigate, user]);

  const handleResendCode = async () => {
    setError("");
    try {
      await resendConfirmationCode(email);
      setSuccessMessage("Verification code resent to your email.");
    } catch (err: any) {
      setError(err.message || "Failed to resend verification code");
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError("");
    setLoading(true);

    try {
      // Step 1: Confirm the email
      await confirmSignUp(email, confirmationCode);

      // If user is already logged in (from login attempt), just refresh auth
      if (isFromLogin) {
        setSuccessMessage("Email verified successfully! Redirecting...");
        await refreshAuth();
        // Navigate to trees page after successful verification
        setTimeout(() => navigate("/trees"), 1500);
        return;
      }

      // Step 2: Auto-login the user (for signup flow)
      if (!password) {
        // Fallback: redirect to login if password wasn't provided
        setSuccessMessage(
          "Account confirmed successfully! Please log in to continue.",
        );
        navigate("/login", { state: { email } });
        return;
      }

      try {
        await signIn(email, password);
        // Step 3: Refresh auth state to get user info
        await refreshAuth();

        // Step 4: Navigate to settings page to complete subscription
        // Product type is preserved in localStorage for the settings page to use
        navigate("/settings");
      } catch (loginError: any) {
        console.error("Auto-login failed:", loginError);
        // Fallback to manual login
        setSuccessMessage(
          "Account confirmed successfully! Please log in to continue.",
        );
        navigate("/login", { state: { email } });
      }
    } catch (error: any) {
      setError(error.message || "Failed to confirm account");
    } finally {
      setLoading(false);
    }
  };

  return (
    <Container maxWidth="sm">
      <Box
        display="flex"
        flexDirection="column"
        alignItems="center"
        justifyContent="center"
        minHeight="80vh"
      >
        <Card sx={{ width: "100%", maxWidth: 500 }}>
          <CardContent sx={{ p: 4 }}>
            <Typography
              variant="h4"
              component="h1"
              textAlign="center"
              gutterBottom
              sx={{ color: "primary.main" }}
            >
              {isFromLogin ? "Verify Your Email" : "Confirm Account"}
            </Typography>
            <Typography
              variant="body1"
              textAlign="center"
              color="text.secondary"
              gutterBottom
            >
              {isFromLogin
                ? "Your account requires email verification before you can continue."
                : "Please check your email and enter the confirmation code below."}
            </Typography>

            {error && (
              <Alert severity="error" sx={{ mb: 2 }}>
                {error}
              </Alert>
            )}

            {successMessage && (
              <Alert severity="success" sx={{ mb: 2 }}>
                {successMessage}
              </Alert>
            )}

            <Box component="form" onSubmit={handleSubmit}>
              <TextField
                fullWidth
                label="Email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                margin="normal"
                required
                disabled={isFromLogin}
              />
              {!isFromLogin && (
                <TextField
                  fullWidth
                  label="Password"
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  margin="normal"
                  required
                  autoComplete="current-password"
                  helperText="Enter your password to automatically sign in after confirmation"
                />
              )}
              <TextField
                fullWidth
                label="Confirmation Code"
                value={confirmationCode}
                onChange={(e) => setConfirmationCode(e.target.value)}
                margin="normal"
                required
                placeholder="Enter the 6-digit code"
              />
              <Button
                type="submit"
                fullWidth
                variant="contained"
                size="large"
                disabled={loading}
                sx={{ mt: 3, mb: 2 }}
              >
                {loading ? "Confirming..." : "Confirm Account"}
              </Button>

              <Button
                fullWidth
                variant="outlined"
                size="small"
                onClick={handleResendCode}
                disabled={!email}
                sx={{ mt: 1, mb: 2 }}
              >
                Resend verification code
              </Button>
            </Box>

            <Box textAlign="center">
              <Button variant="text" onClick={() => navigate("/login")}>
                Back to Sign In
              </Button>
            </Box>
          </CardContent>
        </Card>
      </Box>
    </Container>
  );
};

export default ConfirmAccountPage;
